import type { Flashcard } from '../types';

interface GenerateStandaloneOptions {
  cards: Flashcard[];
  deckName?: string;
}

export const generateStandaloneMatchGame = ({ cards, deckName }: GenerateStandaloneOptions): string => {
  const gameData = cards.slice(0, Math.min(cards.length, 12)).map((card, index) => ({
    term: card.front,
    definition: card.back,
    pairId: index,
    termIndex: index,
    defIndex: index,
  }));

  const gameJson = JSON.stringify(gameData);
  const title = deckName ? `${deckName} - Match Game` : 'Flashcard Match Game';
  const encodedData = encodeURIComponent(gameJson);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${title}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    
    :root {
      --bg-light: #f9fafb;
      --bg-dark: #111827;
      --surface-light: #ffffff;
      --surface-dark: #1f2937;
      --text-light: #111827;
      --text-dark: #f3f4f6;
      --primary: #2563eb;
      --primary-dark: #60a5fa;
      --success: #16a34a;
      --error: #dc2626;
      --border-light: #e5e7eb;
      --border-dark: #374151;
    }
    
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      min-height: 100vh;
      background: var(--bg-light);
      color: var(--text-light);
      transition: background 0.3s, color 0.3s;
    }
    
    body.dark {
      background: var(--bg-dark);
      color: var(--text-dark);
    }
    
    .container {
      max-width: 1200px;
      margin: 0 auto;
      padding: 2rem;
    }
    
    header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 2rem;
    }
    
    h1 {
      font-size: 1.5rem;
      font-weight: 700;
    }
    
    .theme-toggle {
      background: var(--surface-light);
      border: 1px solid var(--border-light);
      padding: 0.5rem 1rem;
      border-radius: 0.5rem;
      cursor: pointer;
      font-size: 0.875rem;
      transition: all 0.2s;
    }
    
    .dark .theme-toggle {
      background: var(--surface-dark);
      border-color: var(--border-dark);
      color: var(--text-dark);
    }
    
    .theme-toggle:hover { opacity: 0.8; }
    
    .stats {
      display: flex;
      justify-content: center;
      gap: 2rem;
      margin-bottom: 2rem;
      font-size: 0.875rem;
      color: #6b7280;
    }
    
    .dark .stats { color: #9ca3af; }
    
    .stats span { display: flex; align-items: center; gap: 0.5rem; }
    
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2rem;
    }
    
    @media (max-width: 768px) {
      .grid { grid-template-columns: 1fr; }
    }
    
    .column-label {
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: #6b7280;
      margin-bottom: 1rem;
    }
    
    .dark .column-label { color: #9ca3af; }
    
    .card {
      background: var(--surface-light);
      border: 2px solid var(--border-light);
      border-radius: 0.75rem;
      padding: 1rem;
      cursor: pointer;
      transition: all 0.2s;
      display: flex;
      align-items: center;
      gap: 0.75rem;
      margin-bottom: 0.75rem;
    }
    
    .dark .card {
      background: var(--surface-dark);
      border-color: var(--border-dark);
    }
    
    .card:hover:not(.matched):not(.disabled) {
      border-color: var(--primary);
      transform: scale(1.01);
    }
    
    .dark .card:hover:not(.matched):not(.disabled) {
      border-color: var(--primary-dark);
    }
    
    .card.selected {
      border-color: var(--primary);
      background: #eff6ff;
    }
    
    .dark .card.selected { background: #1e3a5f; }
    
    .card.matched {
      border-color: var(--success);
      background: #f0fdf4;
      cursor: default;
    }
    
    .dark .card.matched { background: #14532d; }
    
    .card.wrong {
      border-color: var(--error);
      background: #fef2f2;
      animation: shake 0.3s;
    }
    
    .dark .card.wrong { background: #7f1d1d; }
    
    .card.correct {
      border-color: var(--success);
      background: #f0fdf4;
    }
    
    .dark .card.correct { background: #14532d; }
    
    @keyframes shake {
      0%, 100% { transform: translateX(0); }
      25% { transform: translateX(-4px); }
      75% { transform: translateX(4px); }
    }
    
    .card-icon {
      width: 2rem;
      height: 2rem;
      border-radius: 9999px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 700;
      font-size: 0.75rem;
      flex-shrink: 0;
      background: #e5e7eb;
      color: #374151;
    }
    
    .dark .card-icon { background: #374151; color: #f3f4f6; }
    
    .card.matched .card-icon,
    .card.correct .card-icon { background: var(--success); color: white; }
    .card.wrong .card-icon { background: var(--error); color: white; }
    .card.selected .card-icon { background: var(--primary); color: white; }
    
    .card-content { font-size: 0.875rem; font-medium: 500; }
    .card.matched .card-content { color: var(--success); }
    
    .message {
      text-align: center;
      margin-top: 2rem;
      font-size: 0.875rem;
      color: #6b7280;
    }
    
    .dark .message { color: #9ca3af; }
    
    .complete-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 100;
    }
    
    .complete-modal {
      background: var(--surface-light);
      border-radius: 1rem;
      padding: 2rem;
      text-align: center;
      max-width: 400px;
      width: 90%;
    }
    
    .dark .complete-modal { background: var(--surface-dark); }
    
    .complete-modal h2 {
      font-size: 1.5rem;
      margin-bottom: 1rem;
    }
    
    .complete-modal .trophy {
      font-size: 4rem;
      margin-bottom: 1rem;
    }
    
    .complete-stats {
      display: flex;
      justify-content: center;
      gap: 2rem;
      margin: 1.5rem 0;
    }
    
    .complete-stats div { text-align: center; }
    .complete-stats .value { font-size: 1.5rem; font-weight: 700; }
    .complete-stats .label { font-size: 0.75rem; color: #6b7280; }
    
    .dark .complete-stats .label { color: #9ca3af; }
    
    .btn {
      background: var(--primary);
      color: white;
      border: none;
      padding: 0.75rem 1.5rem;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      font-weight: 600;
      cursor: pointer;
      transition: opacity 0.2s;
    }
    
    .btn:hover { opacity: 0.9; }
    
    .btn-secondary {
      background: transparent;
      color: var(--text-light);
      border: 1px solid var(--border-light);
      margin-left: 0.5rem;
    }
    
    .dark .btn-secondary {
      color: var(--text-dark);
      border-color: var(--border-dark);
    }
  </style>
</head>
<body>
  <div class="container">
    <header>
      <h1 id="title">${title}</h1>
      <button class="theme-toggle" onclick="toggleTheme()">🌙 Dark</button>
    </header>
    
    <div class="stats">
      <span>⏱️ <span id="timer">0:00</span></span>
      <span>🎯 <span id="moves">0</span> pairs</span>
      <span>❌ <span id="mistakes">0</span> mistakes</span>
    </div>
    
    <div class="grid">
      <div>
        <div class="column-label">Terms (<span id="terms-matched">0</span>/<span id="terms-total">0</span>)</div>
        <div id="terms"></div>
      </div>
      <div>
        <div class="column-label">Definitions (<span id="defs-matched">0</span>/<span id="defs-total">0</span>)</div>
        <div id="definitions"></div>
      </div>
    </div>
    
    <div class="message" id="message">Click a term to start matching</div>
  </div>
  
  <div id="complete-overlay" class="complete-overlay" style="display: none;">
    <div class="complete-modal">
      <div class="trophy">🏆</div>
      <h2>Congratulations!</h2>
      <p>You matched all pairs!</p>
      <div class="complete-stats">
        <div><div class="value" id="final-accuracy">0%</div><div class="label">Accuracy</div></div>
        <div><div class="value" id="final-time">0:00</div><div class="label">Time</div></div>
        <div><div class="value" id="final-moves">0</div><div class="label">Moves</div></div>
      </div>
      <button class="btn" onclick="restart()">Play Again</button>
      <button class="btn btn-secondary" onclick="closeModal()">Close</button>
    </div>
  </div>

  <script>
    const gameData = ${gameJson};
    let selectedTermIndex = null;
    let moves = 0;
    let mistakes = 0;
    let timeElapsed = 0;
    let timerInterval = null;
    let matchedPairs = new Set();

    function init() {
      renderTerms();
      renderDefinitions();
      updateStats();
      startTimer();

      if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
        document.body.classList.add('dark');
        updateThemeButton();
      }
    }

    function renderTerms() {
      const container = document.getElementById('terms');
      container.innerHTML = gameData.map((item, i) => {
        const isMatched = matchedPairs.has(item.pairId);
        const isSelected = selectedTermIndex === item.termIndex;
        const classes = ['card'];
        if (isMatched) classes.push('matched');
        if (isSelected) classes.push('selected');

        return '<div class="' + classes.join(' ') + '" data-pairid="' + item.pairId + '" data-termindex="' + item.termIndex + '" onclick="selectTerm(' + item.termIndex + ', ' + item.pairId + ')">' +
          '<div class="card-icon">' + (isMatched ? '✓' : (i + 1)) + '</div>' +
          '<div class="card-content">' + escapeHtml(item.term) + '</div>' +
        '</div>';
      }).join('');
      document.getElementById('terms-total').textContent = gameData.length;
      document.getElementById('terms-matched').textContent = matchedPairs.size;
    }

    function renderDefinitions() {
      const shuffled = [...gameData].sort(() => Math.random() - 0.5);
      const container = document.getElementById('definitions');
      container.innerHTML = shuffled.map((item, i) => {
        const isMatched = matchedPairs.has(item.pairId);
        const classes = ['card'];
        if (isMatched) classes.push('matched');

        return '<div class="' + classes.join(' ') + '" data-pairid="' + item.pairId + '" onclick="selectDef(' + item.pairId + ')">' +
          '<div class="card-icon">' + (isMatched ? '✓' : '🔗') + '</div>' +
          '<div class="card-content">' + escapeHtml(item.definition) + '</div>' +
        '</div>';
      }).join('');
      document.getElementById('defs-total').textContent = gameData.length;
      document.getElementById('defs-matched').textContent = matchedPairs.size;
    }

    function selectTerm(termIndex, pairId) {
      if (matchedPairs.has(pairId)) return;
      selectedTermIndex = termIndex;
      renderTerms();
      document.getElementById('message').textContent = 'Now click the matching definition';
    }

    function selectDef(pairId) {
      if (selectedTermIndex === null || matchedPairs.has(pairId)) return;

      moves++;

      const termEl = document.querySelector('#terms .card[data-pairid="' + pairId + '"]');

      if (termEl && termEl.dataset.termindex !== undefined) {
        const termPairId = parseInt(termEl.dataset.pairid);
        matchedPairs.add(termPairId);
        selectedTermIndex = null;

        renderTerms();
        renderDefinitions();
        updateStats();
        document.getElementById('message').textContent = 'Correct! Well done!';

        if (matchedPairs.size === gameData.length) {
          gameComplete();
        }
      } else {
        mistakes++;
        document.getElementById('message').textContent = 'Wrong match! Try again...';

        const wrongDefEl = document.querySelector('#definitions .card[data-pairid="' + pairId + '"]');

        if (termEl) {
          termEl.classList.add('wrong');
          setTimeout(() => termEl.classList.remove('wrong'), 600);
        }
        if (wrongDefEl) {
          wrongDefEl.classList.add('wrong');
          setTimeout(() => wrongDefEl.classList.remove('wrong'), 600);
        }

        selectedTermIndex = null;
        renderTerms();

        setTimeout(() => {
          document.getElementById('message').textContent = 'Click a term to start matching';
        }, 800);
      }
    }

    function updateStats() {
      document.getElementById('moves').textContent = moves;
      document.getElementById('mistakes').textContent = mistakes;
    }

    function startTimer() {
      timerInterval = setInterval(() => {
        timeElapsed++;
        const mins = Math.floor(timeElapsed / 60);
        const secs = timeElapsed % 60;
        document.getElementById('timer').textContent = mins + ':' + (secs < 10 ? '0' : '') + secs;
      }, 1000);
    }

    function stopTimer() {
      if (timerInterval) clearInterval(timerInterval);
    }

    function gameComplete() {
      stopTimer();
      const accuracy = moves > 0 ? Math.round(((moves - mistakes) / moves) * 100) : 100;
      const mins = Math.floor(timeElapsed / 60);
      const secs = timeElapsed % 60;
      
      document.getElementById('final-accuracy').textContent = accuracy + '%';
      document.getElementById('final-time').textContent = mins + ':' + (secs < 10 ? '0' : '') + secs;
      document.getElementById('final-moves').textContent = moves;
      document.getElementById('complete-overlay').style.display = 'flex';
    }

    function restart() {
      closeModal();
      matchedPairs.clear();
      selectedTermIndex = null;
      moves = 0;
      mistakes = 0;
      timeElapsed = 0;
      renderTerms();
      renderDefinitions();
      updateStats();
      startTimer();
      document.getElementById('message').textContent = 'Click a term to start matching';
    }

    function closeModal() {
      document.getElementById('complete-overlay').style.display = 'none';
    }

    function toggleTheme() {
      document.body.classList.toggle('dark');
      updateThemeButton();
    }

    function updateThemeButton() {
      const btn = document.querySelector('.theme-toggle');
      btn.textContent = document.body.classList.contains('dark') ? '☀️ Light' : '🌙 Dark';
    }

    function escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    init();
  </script>
</body>
</html>`;
};

export const downloadStandaloneGame = (options: GenerateStandaloneOptions) => {
  const html = generateStandaloneMatchGame(options);
  const blob = new Blob([html], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  
  const filename = options.deckName 
    ? `${options.deckName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-match-game.html`
    : 'flashcard-match-game.html';
  
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};